import { useCallback } from 'react'
import { useNetworkScope } from './useNetworkScope'

// The one idiom for building an in-app link (TDC-1609).
//
//   const { p } = usePortalPath()
//   navigate(p('/video-production/orders'))
//   <Link to={p('/experts')} />
//
// `p` prefixes the brand-portal base path onto a rooted path. It is correct in
// every context WITHOUT any role check, because basePath is derived from where
// the code is running, not from who is running it:
//
//   brand client, anywhere          basePath '/portal/acme'  -> /portal/acme/experts
//   internal staff, internal pages  basePath ''              -> /experts
//   internal staff, in a workspace  basePath '/portal/acme'  -> /portal/acme/experts
//   anyone on a public share page   basePath '' (PortalScope)-> /experts
//
// That property is the whole point: the same shared component is right on all
// four surfaces, so the sweep is mechanical rather than a judgement call per
// call site.
//
// Non-rooted values pass through untouched — relative paths ('../discovered'),
// absolute URLs, mailto:, tel: and bare '#anchor' — because several call sites
// build their argument at runtime.
//
// Greppable on purpose: `grep -rn "p('/" src` is the sweep-completeness check.
export function usePortalPath() {
  const { basePath } = useNetworkScope()
  const p = useCallback(
    (path: string) => {
      if (typeof path !== 'string' || !path.startsWith('/')) return path
      // '//evil.com' is protocol-relative, not an in-app path.
      if (path.startsWith('//')) return path
      return `${basePath}${path}`
    },
    [basePath],
  )
  return { basePath, p }
}
